import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery } from '@apollo/client';
import { format, parseISO } from 'date-fns';
import { GET_CAR_BOOKINGS } from '../../services/bookingService/queries';
import { BookingStatus } from '../../../__generated__/graphql';
import { URLS } from '../../../constants/urls';
import LoadingSpinner from '../LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  ${tw`w-full max-w-4xl p-4`}
`;

const Title = styled.h2`
  ${tw`text-3xl font-bold mb-6 text-text-primary`}
`;

const FilterWrapper = styled.div`
  ${tw`relative mb-8 w-full md:w-64`}
`;

const FilterButton = styled.div`
  ${tw`flex items-center justify-between p-3 border border-border-default rounded-md bg-white cursor-pointer transition-colors`}

  &:hover {
    ${tw`border-gray-400`}
  }
`;

const FilterDropdown = styled.div`
  ${tw`absolute top-full left-0 w-full mt-1 bg-white border border-border-default rounded-md shadow-card-hover z-10 overflow-hidden`}
`;

const FilterOption = styled.div`
  ${tw`p-3 cursor-pointer text-sm transition-colors`}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const MonthHeader = styled.h3`
  ${tw`text-xs font-bold text-text-secondary uppercase tracking-wider mt-8 mb-4`}
`;

const TripList = styled.div`
  ${tw`space-y-4`}
`;

const TripItem = styled.div`
  ${tw`
    flex items-center justify-between
    p-4
    border border-border-default
    rounded-card
    bg-white
    shadow-card
    transition-all
    duration-200
  `}

  &:hover {
    ${tw`shadow-card border-border-default`}
  }
`;

const TripInfo = styled.div`
  ${tw`flex flex-col gap-1 flex-1`}
`;

const GuestTripName = styled.h4`
  ${tw`text-base font-bold text-text-primary`}
`;

const CarModel = styled.p`
  ${tw`text-sm text-text-secondary font-medium`}
`;

const TripDates = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const TripImage = styled.img`
  ${tw`w-24 h-16 rounded-lg object-cover ml-4 flex-shrink-0 shadow-card`}
`;

const NoTrips = styled.div`
  ${tw`text-center py-20 text-text-secondary`}
`;

interface CarTripHistoryProps {
  carId: string;
}

const toDate = (dateAt: any): Date => {
  if (!dateAt) return new Date();
  if (dateAt instanceof Date) return dateAt;
  if (typeof dateAt === 'string') return parseISO(dateAt);
  return new Date(dateAt);
};

const CarTripHistory: React.FC<CarTripHistoryProps> = ({ carId }) => {
  const [filter, setFilter] = useState<'past' | 'cancelled'>('past');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_CAR_BOOKINGS, {
    variables: { carId },
    fetchPolicy: 'cache-and-network',
  });

  const filteredTrips = useMemo(() => {
    if (!data?.carBookings) return [];

    return data.carBookings
      .filter((trip: any) => {
        const isCancelled = [
          BookingStatus.CancelledByAdmin,
          BookingStatus.CancelledByGuestAfterTripStart,
          BookingStatus.CancelledByGuestLessThan_24HoursBeforeTripStart,
          BookingStatus.CancelledByGuestMoreThan_24Hours,
          BookingStatus.CancelledByHost,
          BookingStatus.CardDeclined,
        ].includes(trip.status);

        if (filter === 'cancelled') {
          return isCancelled;
        } else {
          return [BookingStatus.Booked, BookingStatus.Completed, BookingStatus.Shortened].includes(
            trip.status,
          );
        }
      })
      .sort((a: any, b: any) => toDate(b.startAt).getTime() - toDate(a.startAt).getTime());
  }, [data?.carBookings, filter]);

  const groupedTrips = useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    filteredTrips.forEach((trip: any) => {
      const date = toDate(trip.startAt);
      const monthYear = format(date, 'MMMM yyyy').toUpperCase();
      if (!groups[monthYear]) {
        groups[monthYear] = [];
      }
      groups[monthYear].push(trip);
    });
    return groups;
  }, [filteredTrips]);

  if (loading) return <LoadingSpinner fullScreen color="var(--color-black)" />;
  if (error) return <Container>Error loading trip history: {error.message}</Container>;

  return (
    <Container>
      <Title>Trip History</Title>

      <FilterWrapper>
        <FilterButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <span tw="text-sm font-medium">
            {filter === 'past' ? 'Past trips' : 'Cancelled trips'}
          </span>
          <FontAwesomeIcon
            icon={isDropdownOpen ? faChevronUp : faChevronDown}
            tw="text-xs text-text-secondary"
          />
        </FilterButton>
        {isDropdownOpen && (
          <FilterDropdown>
            <FilterOption
              onClick={() => {
                setFilter('past');
                setIsDropdownOpen(false);
              }}
            >
              Past trips
            </FilterOption>
            <FilterOption
              onClick={() => {
                setFilter('cancelled');
                setIsDropdownOpen(false);
              }}
            >
              Cancelled trips
            </FilterOption>
          </FilterDropdown>
        )}
      </FilterWrapper>

      {Object.keys(groupedTrips).length === 0 ? (
        <NoTrips>No {filter} found for this vehicle.</NoTrips>
      ) : (
        Object.keys(groupedTrips).map(monthYear => (
          <React.Fragment key={monthYear}>
            <MonthHeader>{monthYear}</MonthHeader>
            <TripList>
              {groupedTrips[monthYear].map(trip => {
                const start = toDate(trip.startAt);
                const end = toDate(trip.endAt);
                const dateRange = `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;

                return (
                  <TripItem
                    key={trip.id}
                    onClick={() => {
                      // Navigate to booking details page (host view)
                      if (trip.id) {
                        navigate(`/cars/host/booking/${trip.id}`);
                      }
                    }}
                  >
                    <TripInfo>
                      <GuestTripName>
                        {trip.guestDisplayName ||
                          trip.guest?.displayName ||
                          `${trip.guest?.firstName || ''}'s trip`.trim()}
                      </GuestTripName>
                      <CarModel>
                        {trip.car?.make} {trip.car?.model} {trip.car?.year}
                      </CarModel>
                      <TripDates>{dateRange}</TripDates>
                    </TripInfo>
                    <TripImage
                      src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${trip.car?.thumbnailUrl}`}
                      alt="Car"
                      onError={(e: any) => {
                        e.target.src = '/placeholder-car.png';
                      }}
                    />
                  </TripItem>
                );
              })}
            </TripList>
          </React.Fragment>
        ))
      )}
    </Container>
  );
};

export default CarTripHistory;
